import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useMediaOwnerCheck } from "@/hooks/useMediaOwnerCheck";
import { useMediaOwnerRole } from "@/hooks/useMediaOwnerRole";
import { MediaOwnerStats } from "@/components/media-owner/MediaOwnerStats";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  MapPin, CheckSquare, LogOut, Monitor, TrendingUp, Bell, ShieldOff,
  Users, BarChart3, FileText, HeadphonesIcon, UserCircle, Menu, X,
  ChevronRight, HelpCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { NotificationsCenter } from "@/components/media-owner/NotificationsCenter";
import { DashboardTour } from "@/components/media-owner/DashboardTour";
import { useDashboardTour } from "@/hooks/useDashboardTour";
import rotanaSignsLogo from "@/assets/rotana-signs-logo.png";
import waveMediaLogo from "@/assets/wave-media-logo.png";
import { locations as allLocationsData } from "@/data/locations";

// Lazy-load tab components — deferred until first use
const LocationsManager = lazy(() => import("@/components/media-owner/LocationsManager").then(m => ({ default: m.LocationsManager })));
const CampaignApprovals = lazy(() => import("@/components/media-owner/CampaignApprovals").then(m => ({ default: m.CampaignApprovals })));
const RevenueOverview = lazy(() => import("@/components/media-owner/RevenueOverview").then(m => ({ default: m.RevenueOverview })));
const ActivityFeed = lazy(() => import("@/components/media-owner/ActivityFeed").then(m => ({ default: m.ActivityFeed })));
const TeamManager = lazy(() => import("@/components/media-owner/TeamManager").then(m => ({ default: m.TeamManager })));
const AnalyticsPanel = lazy(() => import("@/components/media-owner/AnalyticsPanel").then(m => ({ default: m.AnalyticsPanel })));
const DocumentsManager = lazy(() => import("@/components/media-owner/DocumentsManager").then(m => ({ default: m.DocumentsManager })));
const SupportRequest = lazy(() => import("@/components/media-owner/SupportRequest").then(m => ({ default: m.SupportRequest })));
const ProfileSettings = lazy(() => import("@/components/media-owner/ProfileSettings").then(m => ({ default: m.ProfileSettings })));


const TabSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-32 w-full rounded-2xl" />
    <Skeleton className="h-64 w-full rounded-2xl" />
  </div>
);

const MEDIA_OWNER_BRANDING: Record<string, { logo?: string; accentColor?: string; gradient?: string }> = {
  "Rotana Signs": {
    logo: rotanaSignsLogo,
    accentColor: "#0b7b41",
    gradient: "from-[#0b7b41]/20 via-[#0b7b41]/8 to-transparent",
  },
  "Wave Media": {
    logo: waveMediaLogo,
    accentColor: "#22d3ee",
    gradient: "from-[#22d3ee]/20 via-[#22d3ee]/8 to-transparent",
  },
};

type TabKey =
  | "locations" | "campaigns" | "revenue" | "analytics"
  | "activity" | "documents" | "team" | "support" | "profile";

const MediaOwnerDashboard = () => {
  const { language } = useLanguage();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMediaOwner, mediaOwnerName, checking } = useMediaOwnerCheck();
  const permissions = useMediaOwnerRole(mediaOwnerName);

  const [activeTab, setActiveTab] = useState<TabKey>("locations");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { showTour, startTour, endTour } = useDashboardTour();
  const [stats, setStats] = useState({ totalLocations: 0, totalScreens: 0, activeCampaigns: 0, maintenanceCount: 0, totalRevenue: 0 });
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!checking && !user) navigate('/auth');
  }, [user, checking, navigate]);

  useEffect(() => {
    if (!isMediaOwner || !mediaOwnerName) return;
    const fetchStats = async () => {
      const { data: locData } = await supabase
        .from('media_owner_locations').select('*').eq('media_owner_name', mediaOwnerName);
      const locations = locData || [];
      const totalScreens = locations.reduce((sum, l) => sum + (l.screen_count_override || 0), 0);
      const maintenanceCount = locations.filter(l => l.maintenance_status !== 'operational').length;

      const { data: approvalData } = await supabase
        .from('media_owner_campaign_approvals').select('id, status, campaign_id').eq('media_owner_name', mediaOwnerName);
      
      // Verify campaigns still exist in saved_campaigns
      const campaignIds = (approvalData || []).map(a => a.campaign_id);
      const { data: existingCampaigns } = campaignIds.length > 0
        ? await supabase.from('saved_campaigns').select('id').in('id', campaignIds)
        : { data: [] as { id: string }[] };
      const existingIds = new Set((existingCampaigns || []).map(c => c.id));
      const validApprovals = (approvalData || []).filter(a => existingIds.has(a.campaign_id));
      
      const approvedApprovals = validApprovals.filter(a => a.status === 'approved');
      const pendingApprovals = validApprovals.filter(a => a.status === 'pending');
      setPendingCount(pendingApprovals.length);

      let totalRevenue = 0;
      if (approvedApprovals.length > 0) {
        const { data: revData } = await supabase
          .from('saved_campaigns').select('total_budget').in('id', approvedApprovals.map(a => a.campaign_id));
        totalRevenue = (revData || []).reduce((s, c) => s + (c.total_budget || 0), 0);
      }

      const allLocations = allLocationsData;
      const ownedHardcodedLocations = allLocations.filter((location) => location.mediaOwner === mediaOwnerName);
      const hardcodedCount = ownedHardcodedLocations.length;
      const ownerAddedCount = locations.filter(l => l.added_by_owner).length;
      const hardcodedScreens = ownedHardcodedLocations.reduce((sum, location) => sum + location.devices, 0);

      setStats({
        totalLocations: hardcodedCount + ownerAddedCount,
        totalScreens: hardcodedScreens + totalScreens,
        activeCampaigns: approvedApprovals.length,
        maintenanceCount,
        totalRevenue,
      });
    };
    fetchStats();
  }, [isMediaOwner, mediaOwnerName]);

  const handleLogout = useCallback(async () => { await signOut(); navigate('/'); }, [signOut, navigate]);

  const switchTab = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  }, []);

  // Memoized nav items — must be before early returns (hooks rule)
  const navItems = useMemo(() => {
    const all: { key: TabKey; label: string; labelAr: string; icon: React.ElementType; badge?: number; show: boolean }[] = [
      { key: 'locations' as TabKey, label: 'Locations', labelAr: 'المواقع', icon: MapPin, show: true },
      { key: 'campaigns' as TabKey, label: 'Approvals', labelAr: 'الموافقات', icon: CheckSquare, badge: pendingCount > 0 ? pendingCount : undefined, show: true },
      { key: 'revenue' as TabKey, label: 'Revenue', labelAr: 'الإيرادات', icon: TrendingUp, show: permissions.canViewRevenue },
      { key: 'analytics' as TabKey, label: 'Analytics', labelAr: 'التحليلات', icon: BarChart3, show: permissions.canViewAnalytics },
      { key: 'activity' as TabKey, label: 'Activity', labelAr: 'النشاط', icon: Bell, show: permissions.canViewActivity },
      
      { key: 'documents' as TabKey, label: 'Documents', labelAr: 'الوثائق', icon: FileText, show: true },
      { key: 'team' as TabKey, label: 'Team', labelAr: 'الفريق', icon: Users, show: permissions.canManageTeam },
      { key: 'support' as TabKey, label: 'Support', labelAr: 'الدعم', icon: HeadphonesIcon, show: permissions.canSubmitSupport },
      { key: 'profile' as TabKey, label: 'Profile', labelAr: 'الملف', icon: UserCircle, show: true },
    ];
    return all.filter(i => i.show);
  }, [pendingCount, permissions.canViewRevenue, permissions.canViewAnalytics, permissions.canViewActivity, permissions.canManageTeam, permissions.canSubmitSupport]);

  const roleLabel = useMemo(() =>
    permissions.role === 'owner' ? (language === 'ar' ? 'مالك' : 'Owner')
    : permissions.role === 'manager' ? (language === 'ar' ? 'مدير' : 'Manager')
    : permissions.role === 'custom' ? (language === 'ar' ? 'مخصص' : 'Custom')
    : (language === 'ar' ? 'مشاهد' : 'Viewer'),
  [permissions.role, language]);

  if (checking || permissions.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary" />
            <div className="absolute inset-0 rounded-full animate-ping border-2 border-primary/20" style={{ animationDuration: '1.5s' }} />
          </div>
          <p className="text-muted-foreground font-medium">{language === 'ar' ? 'جاري التحقق...' : 'Verifying access...'}</p>
        </div>
      </div>
    );
  }

  if (!isMediaOwner || !mediaOwnerName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center p-8 max-w-sm">
          <div className="p-4 rounded-2xl bg-destructive/10">
            <ShieldOff className="h-10 w-10 text-destructive" />
          </div>
          <h2 className="text-xl font-bold">{language === 'ar' ? 'غير مصرح' : 'Access Denied'}</h2>
          <p className="text-muted-foreground text-sm">{language === 'ar' ? 'ليس لديك صلاحية الوصول إلى لوحة تحكم مالك الوسائط' : 'You are not authorized to access the Media Owner Dashboard.'}</p>
          <Button onClick={() => navigate('/')} className="mt-2">{language === 'ar' ? 'العودة للرئيسية' : 'Go Home'}</Button>
        </div>
      </div>
    );
  }

  const branding = mediaOwnerName ? MEDIA_OWNER_BRANDING[mediaOwnerName] : undefined;

  const initials = permissions.displayName
    ? permissions.displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : (user?.email?.[0] || 'M').toUpperCase();

  return (
    <div className={`min-h-screen bg-background ${language === 'ar' ? 'text-right' : 'text-left'}`}>
      {/* ── HERO HEADER ── */}
      <div data-tour="header" className={`relative overflow-hidden border-b border-border/50`}
        style={{ background: branding?.accentColor
          ? `linear-gradient(135deg, ${branding.accentColor}22 0%, ${branding.accentColor}08 50%, transparent 100%)`
          : `linear-gradient(135deg, hsl(var(--primary)/0.12) 0%, hsl(var(--primary)/0.04) 50%, transparent 100%)` }}>
        {/* Decorative circles */}
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-10"
          style={{ background: branding?.accentColor ? `radial-gradient(circle, ${branding.accentColor}, transparent)` : 'radial-gradient(circle, hsl(var(--primary)), transparent)' }} />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-5"
          style={{ background: branding?.accentColor ? branding.accentColor : 'hsl(var(--primary))' }} />

        <div className="container mx-auto px-4 sm:px-6 py-6 relative z-10">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Logo + Info */}
            <div className="flex items-center gap-4">
              {/* Mobile hamburger */}
              <button
                className="lg:hidden p-2 rounded-xl bg-card border border-border shadow-sm hover:bg-accent transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5 text-foreground" />
              </button>

              {branding?.logo ? (
                mediaOwnerName === 'Wave Media' ? (
                  <img src={branding.logo} alt={`${mediaOwnerName} logo`} className="h-14 w-auto object-contain" />
                ) : (
                  <div
                    className="flex items-center justify-center rounded-2xl p-3 shadow-lg"
                    style={{ backgroundColor: branding.accentColor }}
                  >
                    <img src={branding.logo} alt={`${mediaOwnerName} logo`} className="h-10 w-auto object-contain" />
                  </div>
                )
              ) : (
                <div className="p-3 bg-primary/10 rounded-2xl border border-primary/20">
                  <Monitor className="h-8 w-8 text-primary" />
                </div>
              )}

              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight tracking-tight">{mediaOwnerName}</h1>
                <p className="text-sm text-muted-foreground">{language === 'ar' ? 'لوحة تحكم مالك الوسائط' : 'Media Owner Dashboard'}</p>
                {permissions.displayName && (
                  <p className="text-xs text-muted-foreground/70 mt-0.5">
                    {language === 'ar' ? `مرحباً بعودتك، ${permissions.displayName} 👋` : `Welcome back, ${permissions.displayName} 👋`}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Role + Notifications + Logout */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border shadow-sm">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                  {initials}
                </div>
                <span className="text-xs font-medium text-foreground">{roleLabel}</span>
              </div>
              <div data-tour="notifications">
                <NotificationsCenter
                  mediaOwnerName={mediaOwnerName}
                  onNavigateToTab={(tab) => switchTab(tab)}
                />
              </div>
              <button
                onClick={startTour}
                className="p-2 rounded-xl bg-card border border-border shadow-sm hover:bg-accent transition-colors"
                title={language === 'ar' ? 'جولة تعريفية' : 'Take Tour'}
              >
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </button>
              <Button variant="outline" size="sm" onClick={handleLogout}
                className="rounded-xl border-border/70 hover:border-destructive/50 hover:text-destructive transition-all">
                <LogOut className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">{language === 'ar' ? 'خروج' : 'Logout'}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS (under header, above sidebar/content split) ── */}
      <div className="container mx-auto px-4 sm:px-6 py-5" data-tour="stats">
        <MediaOwnerStats {...stats} />
      </div>

      {/* ── LAYOUT: Sidebar + Content ── */}
      <div className="container mx-auto px-4 sm:px-6 pb-10">
        <div className="flex gap-6 items-start">

          {/* ── DESKTOP SIDEBAR ── */}
          <nav data-tour="sidebar" className="hidden lg:flex flex-col w-56 flex-shrink-0 sticky top-6">
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="p-2 space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      data-tour={`tab-${item.key}`}
                      onClick={() => switchTab(item.key)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group relative
                        ${isActive
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                    >
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-foreground/60 rounded-r-full" />}
                      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-accent-foreground'}`} />
                      <span className="flex-1 text-left">{language === 'ar' ? item.labelAr : item.label}</span>
                      {item.badge != null && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none
                          ${isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-destructive text-destructive-foreground'}`}>
                          {item.badge}
                        </span>
                      )}
                      {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary-foreground/60" />}
                    </button>
                  );
                })}
              </div>

              {/* Logout at bottom */}
              <div className="border-t border-border/50 p-2 mt-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{language === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}</span>
                </button>
              </div>
            </div>
          </nav>

          {/* ── MOBILE SIDEBAR OVERLAY ── */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex">
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              <div className="relative w-64 bg-card border-r border-border shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <span className="font-semibold text-foreground">{language === 'ar' ? 'القائمة' : 'Navigation'}</span>
                  <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-accent">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.key;
                    return (
                      <button
                        key={item.key}
                        onClick={() => switchTab(item.key)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                          ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{language === 'ar' ? item.labelAr : item.label}</span>
                        {item.badge != null && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground">{item.badge}</span>
                        )}
                      </button>
                    );
                  })}
                </nav>
                <div className="border-t border-border p-2">
                  <button onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all">
                    <LogOut className="h-4 w-4" />
                    <span>{language === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── CONTENT AREA ── */}
          <main className="flex-1 min-w-0">
            <div data-tour={`content-${activeTab}`} className="w-full">
              <Suspense fallback={<TabSkeleton />}>
                {activeTab === 'locations' && (
                  <LocationsManager mediaOwnerName={mediaOwnerName} canEdit={permissions.canEdit}
                    canAddLocation={permissions.canAddLocation} canDeleteLocation={permissions.canDeleteLocation} />
                )}
                {activeTab === 'campaigns' && (
                  <CampaignApprovals mediaOwnerName={mediaOwnerName} onPendingCountChange={setPendingCount}
                    canApprove={permissions.canApprove} canReject={permissions.canReject} />
                )}
                {activeTab === 'revenue' && permissions.canViewRevenue && (
                  <RevenueOverview mediaOwnerName={mediaOwnerName} />
                )}
                {activeTab === 'analytics' && permissions.canViewAnalytics && (
                  <AnalyticsPanel mediaOwnerName={mediaOwnerName} />
                )}
                {activeTab === 'activity' && permissions.canViewActivity && (
                  <ActivityFeed mediaOwnerName={mediaOwnerName} />
                )}
                {activeTab === 'documents' && (
                  <DocumentsManager mediaOwnerName={mediaOwnerName} canUpload={permissions.canUpload}
                    canDeleteDocument={permissions.canDeleteDocument} />
                )}
                {activeTab === 'team' && permissions.canManageTeam && (
                  <TeamManager mediaOwnerName={mediaOwnerName} canManageTeam={permissions.canManageTeam} isPrimary={permissions.isPrimary} />
                )}
                {activeTab === 'support' && permissions.canSubmitSupport && (
                  <SupportRequest mediaOwnerName={mediaOwnerName} />
                )}
                {activeTab === 'profile' && (
                  <ProfileSettings mediaOwnerName={mediaOwnerName} role={permissions.role} isPrimary={permissions.isPrimary} />
                )}
              </Suspense>
            </div>
          </main>
        </div>
      </div>

      {/* ── GUIDED TOUR ── */}
      <DashboardTour
        isOpen={showTour}
        onClose={endTour}
        switchTab={switchTab}
        language={language}
        permissions={permissions}
      />
    </div>
  );
};

export default MediaOwnerDashboard;
